/**
 * MailComposeDialog — popup compose editabile per risposta al fornitore.
 *
 * Pattern a11y: role=dialog, aria-modal, Esc + click-fuori (bloccati durante
 * l'invio), focus iniziale sul bottone Annulla (pattern PackagesConfirmDialog).
 *
 * Campi precompilati ma modificabili: recipient, subject, body.
 * Mostra l'allegato (LDV) previsto. Bottone Invia → onSend; Annulla → onClose.
 */
import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";

const TITLE_ID = "mail-compose-dialog-title";

export interface MailComposePayload {
  recipient: string;
  subject: string;
  body: string;
  packages_count?: number | null;
}

export interface MailComposeDialogProps {
  orderRef: string;
  initialRecipient: string;
  initialSubject: string;
  initialBody: string;
  attachmentNames: string[];
  packagesCount?: number | null;
  /** Riceve il payload composto. Il chiamante esegue sendSupplierReply. */
  onSend: (payload: MailComposePayload) => Promise<void> | void;
  onClose: () => void;
  /** Stato invio in corso (gestito dal chiamante, per disabilitare bottoni). */
  sending?: boolean;
  /** Errore invio (gestito dal chiamante). */
  sendError?: string | null;
}

export function MailComposeDialog({
  orderRef,
  initialRecipient,
  initialSubject,
  initialBody,
  attachmentNames,
  packagesCount,
  onSend,
  onClose,
  sending = false,
  sendError = null,
}: MailComposeDialogProps) {
  const [recipient, setRecipient] = useState(initialRecipient);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);

  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !sending) onClose();
    };
    document.addEventListener("keydown", onKey, { capture: true });
    cancelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey, { capture: true });
  }, [onClose, sending]);

  function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget && !sending) onClose();
  }

  function handleSend() {
    if (sending) return;
    void onSend({ recipient, subject, body, packages_count: packagesCount ?? null });
  }

  const sendLabel = sending ? "Invio in corso…" : "Invia";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-xl rounded-lg border border-line bg-card shadow-popover">
        {/* Header */}
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-display text-[17px] font-bold text-ink">
            Rispondi al fornitore
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-2">Ordine {orderRef}</p>
        </header>

        {/* Corpo form */}
        <div className="px-5 py-4 space-y-3">
          {/* Destinatario */}
          <div>
            <label
              htmlFor="compose-recipient"
              className="block text-[11.5px] font-medium text-ink-2 mb-1"
            >
              Destinatario
            </label>
            <input
              id="compose-recipient"
              aria-label="Destinatario"
              type="email"
              value={recipient}
              disabled={sending}
              onChange={(e) => setRecipient(e.target.value)}
              className="w-full rounded border border-line px-3 py-1.5 text-[13px] text-ink placeholder:text-ink-4 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Oggetto */}
          <div>
            <label
              htmlFor="compose-subject"
              className="block text-[11.5px] font-medium text-ink-2 mb-1"
            >
              Oggetto
            </label>
            <input
              id="compose-subject"
              aria-label="Oggetto"
              type="text"
              value={subject}
              disabled={sending}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded border border-line px-3 py-1.5 text-[13px] text-ink placeholder:text-ink-4 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {/* Corpo */}
          <div>
            <label
              htmlFor="compose-body"
              className="block text-[11.5px] font-medium text-ink-2 mb-1"
            >
              Corpo
            </label>
            <textarea
              id="compose-body"
              aria-label="Corpo"
              rows={6}
              value={body}
              disabled={sending}
              onChange={(e) => setBody(e.target.value)}
              className="w-full rounded border border-line px-3 py-1.5 text-[13px] text-ink placeholder:text-ink-4 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-accent resize-y"
            />
          </div>

          {/* Allegati */}
          {attachmentNames.length > 0 && (
            <div>
              <p className="text-[12px] text-ink-2 font-semibold mb-1.5">Allegati</p>
              <ul className="space-y-1">
                {attachmentNames.map((name) => (
                  <li key={name} className="flex items-center gap-1.5 text-[12px] text-ink-2">
                    {/* clip icon inline SVG (no raw color, usa currentColor) */}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      className="text-ink-2 shrink-0"
                    >
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-line px-5 py-3">
          {sendError && (
            <p role="alert" className="mb-2 text-[11.5px] text-red">
              {sendError}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              aria-busy={sending}
              className="rounded-sm border border-accent bg-accent-2 px-[14px] py-2 text-[13px] font-medium text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hook helper per usare MailComposeDialog con stato locale invio
// ---------------------------------------------------------------------------

export interface UseMailComposeResult {
  open: (opts: ComposeOpts) => void;
  dialog: React.ReactNode;
}

export interface ComposeOpts {
  orderRef: string;
  initialRecipient: string;
  initialSubject: string;
  initialBody: string;
  attachmentNames: string[];
  packagesCount?: number | null;
  /** Chiamato dopo invio riuscito. */
  onSuccess?: () => void;
}

/**
 * Hook che gestisce apertura/chiusura + stato invio del MailComposeDialog.
 *
 * Uso:
 *   const { open, dialog } = useMailCompose(sendFn);
 *   // nel return:  {dialog}
 *   // nel handler: open({ orderRef, ... })
 */
export function useMailCompose(
  sendFn: (orderRef: string, payload: MailComposePayload) => Promise<unknown>,
): UseMailComposeResult {
  const [opts, setOpts] = useState<ComposeOpts | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  function open(newOpts: ComposeOpts) {
    setSendError(null);
    setSending(false);
    setOpts(newOpts);
  }

  function handleClose() {
    if (sending) return;
    setOpts(null);
    setSendError(null);
  }

  async function handleSend(payload: MailComposePayload) {
    if (!opts) return;
    const currentOpts = opts;
    setSending(true);
    setSendError(null);
    try {
      await sendFn(currentOpts.orderRef, payload);
      setOpts(null);
      currentOpts.onSuccess?.();
    } catch (err) {
      setSendError(prettifyApiError(err));
    } finally {
      setSending(false);
    }
  }

  const dialog =
    opts !== null ? (
      <MailComposeDialog
        {...opts}
        sending={sending}
        sendError={sendError}
        onSend={handleSend}
        onClose={handleClose}
      />
    ) : null;

  return { open, dialog };
}
